import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Copy, FileJson, X } from 'lucide-react';

import { Button } from '@/shared/ui';

import styles from './ImportFormatDialog.module.css';

/**
 * Przykładowy wsad — ⭐ zgodny z czytnikiem (`ImportFileReader`): `source` raz na górze,
 * płaskie `elements` z `parent`, `owners` jako obiekty `{ type, key, displayName }`,
 * `relations` wyłącznie `depends-on`.
 * Treść przykładu jest kodem, nie tekstem interfejsu — dlatego nie idzie przez i18n.
 */
export const IMPORT_FILE_EXAMPLE = `{
  "source": "repo",
  "elements": [
    {
      "key": "mod/commerce",
      "title": "Commerce",
      "type": "module",
      "description": "Sales and payments",
      "owners": [
        { "type": "team", "key": "platform", "displayName": "Platform" }
      ]
    },
    {
      "key": "svc/payments",
      "parent": "mod/commerce",
      "title": "Payments service",
      "type": "service",
      "owners": [
        { "type": "team", "key": "platform", "displayName": "Platform" },
        { "type": "user", "key": "anna.kowalska@example.com", "displayName": "Anna Kowalska" }
      ]
    },
    {
      "key": "lib/currency",
      "parent": { "source": "libs", "key": "shared" },
      "title": "Currency library",
      "type": "library"
    }
  ],
  "relations": [
    { "type": "depends-on", "from": "svc/payments", "to": "lib/currency" },
    { "from": "svc/payments", "to": { "source": "infra", "key": "db/postgres" } }
  ]
}`;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Okno „Format pliku" na ekranie importu (2026-08-23, słowo usera): jak wygląda wsad JSON,
 * co znaczą pola i na jakich zasadach import działa — <b>bez wychodzenia z ekranu</b>.
 *
 * ⭐ Radix Dialog, nie własne okno jak omnibox: tu są prawdziwe kontrolki (Kopiuj, Zamknij)
 * i długa, przewijana treść — pułapka ogniska i `aria-modal` mają działać naprawdę (spec 12B.3:
 * „Radix na trudne"). Stan otwarcia siedzi w adresie (`?help=format`) — treść da się podlinkować
 * (spec 4A), a odświeżenie nie zamyka okna.
 *
 * ⚠ Cała proza przez klucze i18n (spec 4C); przykład JSON jest kodem i zostaje po angielsku.
 */
export function ImportFormatDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copyExample() {
    try {
      await navigator.clipboard.writeText(IMPORT_FILE_EXAMPLE);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Schowek niedostępny (http, brak zgody) — przykład da się zaznaczyć ręcznie, nic nie udajemy.
      setCopied(false);
    }
  }

  const fields = [
    { key: 'source', required: true },
    { key: 'key', required: true },
    { key: 'parent', required: false },
    { key: 'titleField', required: false, name: 'title' },
    { key: 'type', required: false },
    { key: 'description', required: false },
    { key: 'owners', required: false },
  ] as const;

  const rules = ['noStatement', 'addOnly', 'twoPhases', 'conflicts', 'order', 'cycles', 'limits'] as const;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles['overlay']} />
        <Dialog.Content className={styles['content']} aria-describedby={undefined}>
          <header className={styles['head']}>
            <span className={styles['icon']} aria-hidden="true">
              <FileJson size={18} />
            </span>
            <Dialog.Title className={styles['title']}>{t('import.format.title')}</Dialog.Title>
            <Dialog.Close asChild>
              <button className={styles['close']} type="button" aria-label={t('import.format.close')}>
                <X size={16} aria-hidden="true" />
              </button>
            </Dialog.Close>
          </header>

          <div className={styles['body']}>
            <p className={styles['lead']}>{t('import.format.lead')}</p>

            <section className={styles['section']} aria-labelledby="import-format-example">
              <div className={styles['sectionHead']}>
                <h3 id="import-format-example" className={styles['sectionTitle']}>
                  {t('import.format.example.title')}
                </h3>
                <Button variant="ghost" onClick={() => void copyExample()}>
                  {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                  {copied ? t('import.format.example.copied') : t('import.format.example.copy')}
                </Button>
              </div>
              {/* Przykład nie ma własnego suwaka pionowego (przewija się treść okna), więc nie
                  potrzebuje `tabIndex` — a bez niego nie jest „elementem nieinteraktywnym z fokusem". */}
              <pre className={styles['code']}>
                <code>{IMPORT_FILE_EXAMPLE}</code>
              </pre>
            </section>

            <section className={styles['section']} aria-labelledby="import-format-fields">
              <h3 id="import-format-fields" className={styles['sectionTitle']}>
                {t('import.format.fields.title')}
              </h3>
              <table className={styles['table']}>
                <thead>
                  <tr>
                    <th scope="col">{t('import.format.fields.name')}</th>
                    <th scope="col">{t('import.format.fields.required')}</th>
                    <th scope="col">{t('import.format.fields.meaning')}</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field) => (
                    <tr key={field.key}>
                      <td>
                        <code className={styles['field']}>{'name' in field ? field.name : field.key}</code>
                      </td>
                      <td className={styles['required']}>
                        {field.required ? t('import.format.fields.yes') : t('import.format.fields.no')}
                      </td>
                      <td>{t(`import.format.fields.${field.key}`)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className={styles['section']} aria-labelledby="import-format-relations">
              <h3 id="import-format-relations" className={styles['sectionTitle']}>
                {t('import.format.relations.title')}
              </h3>
              <p className={styles['text']}>{t('import.format.relations.body')}</p>
            </section>

            <section className={styles['section']} aria-labelledby="import-format-rules">
              <h3 id="import-format-rules" className={styles['sectionTitle']}>
                {t('import.format.rules.title')}
              </h3>
              <ul className={styles['rules']}>
                {rules.map((rule) => (
                  <li key={rule}>{t(`import.format.rules.${rule}`)}</li>
                ))}
              </ul>
            </section>
          </div>

          <footer className={styles['foot']}>
            <Dialog.Close asChild>
              <Button variant="primary">{t('import.format.close')}</Button>
            </Dialog.Close>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
